/**
 * Cart Variant Selector
 * Allows customers to change product variants directly from the cart
 */

(function() {
  'use strict';

  class CartVariantSelector {
    constructor() {
      this.selectors = document.querySelectorAll('.cart-variant-select');
      this.init();
    }

    init() {
      if (this.selectors.length === 0) return;

      this.selectors.forEach(select => {
        select.addEventListener('change', (e) => this.handleVariantChange(e));
      });
    }

    handleVariantChange(event) {
      const select = event.target;
      const line = parseInt(select.dataset.line);
      const optionIndex = parseInt(select.dataset.optionIndex);

      // Get all selects for this line item
      const allSelects = document.querySelectorAll(`[data-line="${line}"]`);

      // Build selected options array
      const selectedOptions = [];
      allSelects.forEach(s => {
        const index = parseInt(s.dataset.optionIndex);
        selectedOptions[index] = s.value;
      });

      // Get variant data for this product
      const variantDataScript = document.querySelector(`[data-cart-item-variants="${line}"]`);
      if (!variantDataScript) {
        console.error('Variant data not found');
        return;
      }

      const variantData = JSON.parse(variantDataScript.textContent);

      // Update available options for other selects based on current selection
      this.updateAvailableOptions(line, variantData, selectedOptions);

      // Find matching variant
      const matchingVariant = variantData.variants.find(variant => {
        return (
          variant.option1 === selectedOptions[0] &&
          (selectedOptions[1] === undefined || variant.option2 === selectedOptions[1]) &&
          (selectedOptions[2] === undefined || variant.option3 === selectedOptions[2])
        );
      });

      if (!matchingVariant) {
        console.error('No matching variant found');
        return;
      }

      // Check if variant is available
      if (!matchingVariant.available) {
        alert('Sorry, this variant is currently out of stock.');
        // Revert select to previous value
        const currentVariantId = variantData.currentVariantId;
        const currentVariant = variantData.variants.find(v => v.id === currentVariantId);
        if (currentVariant) {
          select.value = currentVariant[`option${optionIndex + 1}`];
        }
        return;
      }

      // Update cart with new variant
      this.updateCartVariant(line, matchingVariant.id);
    }

    updateAvailableOptions(line, variantData, selectedOptions) {
      const allSelects = document.querySelectorAll(`[data-line="${line}"]`);

      allSelects.forEach(select => {
        const optionIndex = parseInt(select.dataset.optionIndex);

        // Get all available values for this option based on other selected options
        const availableValues = new Set();

        variantData.variants.forEach(variant => {
          // Check if variant matches other selected options
          let matches = true;
          selectedOptions.forEach((value, index) => {
            if (index !== optionIndex && value !== undefined) {
              const optionKey = `option${index + 1}`;
              if (variant[optionKey] !== value) {
                matches = false;
              }
            }
          });

          if (matches && variant.available) {
            availableValues.add(variant[`option${optionIndex + 1}`]);
          }
        });

        // Update options in select
        const options = select.querySelectorAll('option');
        options.forEach(option => {
          if (availableValues.has(option.value)) {
            option.disabled = false;
            option.style.display = '';
          } else {
            option.disabled = true;
            option.style.display = 'none';
          }
        });
      });
    }

    updateCartVariant(line, variantId) {
      // Show loading spinner
      const loadingOverlay = document.getElementById('cartLoadingOverlay');
      if (loadingOverlay) {
        loadingOverlay.classList.add('active');
      }

      // Get current quantity and item key
      const cartRow = document.querySelector(`[data-cart-item-index="${line}"]`);
      const qtyInput = document.querySelector(`[data-cart-item-index="${line}"] [data-quantity-input]`);
      const currentQty = qtyInput ? parseInt(qtyInput.value) : 1;
      const itemKey = cartRow ? cartRow.dataset.cartItemKey : null;

      if (!itemKey) {
        console.error('Cart item key not found');
        if (loadingOverlay) {
          loadingOverlay.classList.remove('active');
        }
        alert('There was an error updating your cart. Please try again.');
        return;
      }

      // Use Shopify's cart update API to change the variant directly
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: itemKey,
          quantity: 0
        })
      })
      .then(response => response.json())
      .then(() => {
        // Add new variant
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: currentQty
          })
        });
      })
      .then(response => response.json())
      .then(() => {
        // Save scroll position before reload
        sessionStorage.setItem('cartScrollPosition', window.scrollY);
        // Reload the page to get fresh cart data
        window.location.reload();
      })
      .catch(error => {
        console.error('Error updating cart:', error);
        if (loadingOverlay) {
          loadingOverlay.classList.remove('active');
        }
        alert('There was an error updating your cart. Please try again.');
      });
    }

    updateCartUI(cart, updatedLine) {
      // Find the new item in the cart (it will be the last one added)
      const newItem = cart.items[cart.items.length - 1];

      if (!newItem) {
        window.location.reload();
        return;
      }

      // Update price for this line item
      const cartRow = document.querySelector(`[data-cart-item-index="${updatedLine}"]`);
      if (cartRow) {
        // Check if item has discount on unit price
        const hasUnitDiscount = newItem.original_price > newItem.final_price;

        // Check if item has discount on line price
        const hasLineDiscount = newItem.original_line_price > newItem.final_line_price;

        // Update unit price in Price column (first data-cart-item-regular-price)
        const priceList = cartRow.querySelector('[data-cart-item-price-list]');
        if (priceList) {
          const unitRegularPriceEl = priceList.querySelector('[data-cart-item-regular-price]');
          if (unitRegularPriceEl) {
            unitRegularPriceEl.textContent = this.formatMoney(newItem.original_price);
          }

          const unitOriginalPriceEl = priceList.querySelector('[data-cart-item-original-price]');
          if (unitOriginalPriceEl) {
            unitOriginalPriceEl.textContent = this.formatMoney(newItem.original_price);
          }

          const unitFinalPriceEl = priceList.querySelector('[data-cart-item-final-price]');
          if (unitFinalPriceEl) {
            unitFinalPriceEl.textContent = this.formatMoney(newItem.final_price);
          }

          // Toggle discount/regular price display for unit price
          const unitDiscountedGroup = priceList.querySelector('[data-cart-item-discounted-price-group]');
          const unitRegularGroup = priceList.querySelector('[data-cart-item-regular-price-group]');

          if (unitDiscountedGroup && unitRegularGroup) {
            if (hasUnitDiscount) {
              unitDiscountedGroup.classList.remove('hide');
              unitRegularGroup.classList.add('hide');
            } else {
              unitDiscountedGroup.classList.add('hide');
              unitRegularGroup.classList.remove('hide');
            }
          }
        }

        // Update line total price in Total column (data-cart-item-line-price)
        const linePriceContainer = cartRow.querySelector('[data-cart-item-line-price]');
        if (linePriceContainer) {
          // Line price elements
          const lineOriginalPriceEl = linePriceContainer.querySelector('[data-cart-item-original-price]');
          if (lineOriginalPriceEl) {
            lineOriginalPriceEl.textContent = this.formatMoney(newItem.original_line_price);
          }

          const lineFinalPriceEl = linePriceContainer.querySelector('[data-cart-item-final-price]');
          if (lineFinalPriceEl) {
            lineFinalPriceEl.textContent = this.formatMoney(newItem.final_line_price);
          }

          const lineRegularPriceEl = linePriceContainer.querySelector('[data-cart-item-regular-price]');
          if (lineRegularPriceEl) {
            lineRegularPriceEl.textContent = this.formatMoney(newItem.original_line_price);
          }

          // Toggle discount/regular price display for line price
          const lineDiscountedGroup = linePriceContainer.querySelector('[data-cart-item-discounted-price-group]');
          const lineRegularGroup = linePriceContainer.querySelector('[data-cart-item-regular-price-group]');

          if (lineDiscountedGroup && lineRegularGroup) {
            if (hasLineDiscount) {
              lineDiscountedGroup.classList.remove('hide');
              lineRegularGroup.classList.add('hide');
            } else {
              lineDiscountedGroup.classList.add('hide');
              lineRegularGroup.classList.remove('hide');
            }
          }
        }
      }

      // Update cart totals
      const cartTotalEls = document.querySelectorAll('[data-cart-total]');
      cartTotalEls.forEach(el => {
        el.textContent = this.formatMoney(cart.total_price);
      });

      // Update cart item count
      const cartCountEls = document.querySelectorAll('[data-cart-count]');
      cartCountEls.forEach(el => {
        el.textContent = cart.item_count;
      });
    }

    formatMoney(cents) {
      // Use Shopify's money format if available, otherwise fallback to simple format
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, Shopify.money_format || '${{amount}}');
      }
      return '$' + (cents / 100).toFixed(2);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new CartVariantSelector());
  } else {
    new CartVariantSelector();
  }
})();
